import { useCallback, useEffect, useMemo } from 'react';
import i18n from 'i18n';
import { FormConfig, FormField } from 'components/common/form/FormConfig';
import { faFileAudio, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useOutdoorDurationFields } from './useOutdoorDurationFields';
import { useFormikContext } from 'formik';
import { floor, isNil } from 'lodash';
import { isImageAudioFamily } from 'core/creative/Creative';
import styles from './outdoorFields.module.scss';
import { useOutdoorProperties } from './useOutdoorProperties';
import { ImageAudioOutdoorCreativeFields } from 'core/creative/OutdoorCreative';
import { OutdoorFormModel } from '../OutdoorFormModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// 「圖+音」素材無聲音檔時，圖片播放時長的可選值（秒）
const DEFAULT_IMAGE_DURATION = 30;

const durationOptions = [
  {
    label: i18n.t('outdoorForm.labels.duration15'),
    value: 15
  },
  {
    label: i18n.t('outdoorForm.labels.duration30'),
    value: DEFAULT_IMAGE_DURATION
  }
];

export const useImageAudioOutdoorFields = <T extends OutdoorFormModel<T>>(
  model: T,
  onSizeInstructionBtnClick: () => void
): FormField[] => {
  const { values, setFieldValue } =
    useFormikContext<ImageAudioOutdoorCreativeFields>();
  const noCreativeSizeLimit = isNil(model.creativeSizes);
  const isEditing = !!values.creativeId;
  const { outdoorType, width, height, duration, useAudio } =
    useOutdoorProperties(values);

  const { supportAudio } = model;

  // 時長由渠道給出區間（電子紙，ECOPD-5659）時一律用分／秒下拉，建立與編輯都可改。
  // 拆成兩個純值而不是直接用 model.durationRange：那支 getter 每次存取都回新物件，
  // 直接放進下面幾個依賴陣列會讓欄位每次 render 都重建。
  const { durationRange } = model;
  const hasDurationRange = durationRange !== undefined;
  const durationMaxSeconds = durationRange
    ? (durationRange.max ?? durationRange.min)
    : undefined;

  // 建立時：無音檔（或該類型不採媒體時長）即可用下拉選時長。
  // 編輯時：僅「允許編輯圖片時長」的頻道（研發測試用 / Sandbox，RKB-2385）才維持下拉，
  // 其餘頻道編輯時仍鎖成唯讀 label（行為不變）。
  const canSelectDuration =
    !hasDurationRange &&
    (!isEditing || model.allowEditImageDuration) &&
    (!useAudio || !model.useMediaDuration);

  const durationFields = useOutdoorDurationFields(
    hasDurationRange ? 'minuteSecond' : canSelectDuration ? 'select' : 'label',
    hasDurationRange ? model.validateDuration : undefined,
    durationOptions,
    durationMaxSeconds
  );

  useEffect(() => {
    if (!isImageAudioFamily(outdoorType)) {
      return;
    }
    // 不支援聲音的渠道（電子紙）把開關關掉：欄位不顯示，但切換素材類型或編輯舊資料時
    // 可能帶進 true，留著會連帶讓時長改吃音檔長度。
    if (!supportAudio && useAudio) {
      setFieldValue('typeProperties.useAudio', false);
    }
    // 編輯既有的「圖+音」素材、且時長為可選下拉時（僅研發測試用會走到），
    // 若原本存的 duration 不在選項（15 / 30）內（例如舊資料），下拉會顯示空值，
    // 這裡直接 fallback 成預設值（30），確保回填的一定是合法選項。
    // 走區間下拉的渠道靠 canSelectDuration 就排除掉了（它第一個條件就是沒有區間），
    // 不然使用者存過的秒數會被重設成 30。
    if (
      isEditing &&
      canSelectDuration &&
      !durationOptions.some((opt) => opt.value === duration)
    ) {
      setFieldValue('typeProperties.duration', DEFAULT_IMAGE_DURATION);
    }
  }, [
    outdoorType,
    isEditing,
    canSelectDuration,
    duration,
    supportAudio,
    useAudio,
    setFieldValue
  ]);

  const allowSizeBoxing = Object.keys(model.sizeBoxingMap).length > 0;

  const onImageFileChange = useCallback(
    (fileData) => {
      if (fileData && noCreativeSizeLimit) {
        setFieldValue(
          'typeProperties.creativeSize',
          `${fileData.width} x ${fileData.height}`
        );
      }
    },
    [noCreativeSizeLimit, setFieldValue]
  );

  const onMediaFileChange = useCallback(
    (fileData) => {
      fileData &&
        setFieldValue('typeProperties.duration', floor(fileData.duration));
    },
    [setFieldValue]
  );

  const onUseAudioChange = useCallback(
    (useAudio: boolean) => {
      if (hasDurationRange) {
        return;
      }
      if (!useAudio && !durationOptions.some((opt) => opt.value === duration)) {
        setFieldValue(
          'typeProperties.duration',
          model.getDefaultDuration(outdoorType, duration)
        );
      }
    },
    [outdoorType, duration, model, hasDurationRange, setFieldValue]
  );

  const fields = useMemo(() => {
    if (!isImageAudioFamily(outdoorType)) {
      return [];
    }

    const builder = new FormConfig.FieldsBuilder();
    builder.addFormikFileInput({
      type: 'image',
      label: i18n.t('creativeSetupFlow.labels.image'),
      name: 'medias.image',
      className: noCreativeSizeLimit
        ? undefined
        : styles[`imageInput${width}x${height}`],
      maxWidth: noCreativeSizeLimit ? 500 : Math.min(width / 3, 500),
      hints: model.getImageHints(width, height),
      permanentHint: allowSizeBoxing ? (
        <div
          className={styles.sizeInstructionBtn}
          onClick={onSizeInstructionBtnClick}
        >
          {i18n.t('outdoorCreativeSizeHint.labels.creativeInfoHint')}
          <FontAwesomeIcon icon={faInfoCircle} />
        </div>
      ) : undefined,
      validate: (value) =>
        noCreativeSizeLimit
          ? model.validateImage(value)
          : model.validateImage(value, width, height),
      onChange: onImageFileChange
    });

    if (supportAudio) {
      builder.addFormikSwitch({
        label: i18n.t('creativeSetupFlow.labels.outdoorAudioSwitch'),
        name: 'typeProperties.useAudio',
        onChange: onUseAudioChange
      });
    }

    if (supportAudio && useAudio) {
      builder.addFormikFileInput({
        type: 'audio',
        label: i18n.t('creativeSetupFlow.labels.audio'),
        name: 'medias.audio',
        className: styles.fileInput,
        icon: faFileAudio,
        validTypes: ['audio/mpeg'],
        hints: model.getAudioHints(),
        validate: model.validateAudio,
        // 時長由使用者自訂的渠道不跟著音檔長度走，否則上傳音檔會蓋掉填好的秒數。
        onChange:
          model.useMediaDuration && !hasDurationRange
            ? onMediaFileChange
            : undefined
      });
    }

    return [...builder.build(), ...durationFields];
  }, [
    model,
    outdoorType,
    width,
    height,
    useAudio,
    durationFields,
    allowSizeBoxing,
    noCreativeSizeLimit,
    hasDurationRange,
    supportAudio,
    onImageFileChange,
    onMediaFileChange,
    onUseAudioChange,
    onSizeInstructionBtnClick
  ]);

  return fields;
};
